import { ProgressReport } from './progress-report.model';
import moment from 'src/app/utils/moment';
import { WHITE_LABEL_ORGANIZATION_IDS } from '../constants/white-label-orgs';


export interface IUserOrganization {
  type: "client" | "trainer",
  status: "active" | "inactive" | "disabled" | "invited" | "lead",
}

export class  User {
  public uid: string;
  public $id: string;
  public deletionDate?: string;
  public firstName: string;
  public lastName: string;
  public email: string;
  public phone?: string;
  public lastLogin?: string;
  /** ISO timestamp of last successful Apple Health / Health Connect sync. Missing = never synced. */
  public healthLastSyncedAt?: string | null;
  public heightUnit?: "in";
  public occupation?: string;
  public address?: string;
  public profileComplete?: boolean;
  public fullAccess?: boolean;
  public type: {client?: boolean, trainer?: boolean};
  public cancelationDate: string;
  public clientType: "in_person" | "online";
  public colorSwitcher?: boolean;
  public organizations: {
    [orgName: string]: IUserOrganization
  };
  public preferedUnits?: "imperial" | "metric";
  public preferredLanguage?: string;
  public trainers?: {} = {};
  public clients?: {} = {};
  public createdBy: string;
  public createdOn: string;
  public birthday: string;
  public marketing?: boolean;
  public plan?: "personal";
  public height?: any;
  public bodyType?: "ectomorph" | "mesomorph" | "endomorph";
  public profilePicture? : string;
  public gender: "male" | "female";
  public service?: string;
  public status: "active" | "past_due" | "canceled" | "invited";
  public instagram?: string;
  public stripeId?: string;
  public actions: {
    equipmentList?: boolean | string,
    foodPreferences?: boolean | string,
    terms?: boolean;
  };
  public progressReports: ProgressReport[];
  public activityLevel?: "sedentary" | "active" | "lightlyActive" | "veryActive";
  public terms? : {
    status: "accepted",
    timestamp: string;
  }
  
  constructor (init: Partial<User>){
      Object.assign(this, init);
  }

  public getDefaultOrganization() {
    if(this.organizations) {
      let name = null;
      let keys = Object.keys(this.organizations);
      if(keys.length === 1) {
        return name = keys[0];
      } else {
        for (const key of keys) {
          if (!WHITE_LABEL_ORGANIZATION_IDS.has(key)) {
            name = key;
            break;
          }
        }
      }
        return name
    } else {
      return undefined
    }
  }

  public getOrganizationIds(): string[] {
    if(this.organizations && Object.keys(this.organizations).length > 0) {
      return Object.keys(this.organizations);
    } else {
      return null
    }
  }

  public getOrganizations(): {id: string, status: IUserOrganization["status"], type: IUserOrganization["type"]}[] {
    if(this.organizations && Object.keys(this.organizations).length > 0) {
      const toReturn = [];
      const orgIds = Object.keys(this.organizations);
      for (const orgId of orgIds) {
        toReturn.push({id: orgId, ...this.organizations[orgId]});
      }
      return toReturn;
    } else {
      return null
    }
  }

  public getOrganization(orgName: string) {
    return this.organizations? this.organizations[orgName] : null;
  }

  public getOrganizationStatus(orgName: string): IUserOrganization["status"] {
    return this.organizations && this.organizations[orgName] && this.organizations[orgName].status? this.organizations[orgName].status : null;
  }

  getClients(): string[] {
    let clients = [];
    if(this.clients && Object.keys(this.clients).length > 0) {
      for (const clientId in this.clients) {
        if (this.clients.hasOwnProperty(clientId)) {
          clients.push(clientId);
        }
      }
    } 
    return clients
  }

  public isClient() {
    let isClient = false;
    const orgIds = this.getOrganizationIds() || [];
    for (const orgId of orgIds) {
      if(this.organizations[orgId]?.type === "client") {
        isClient = true;
        break
      }
    }
    return isClient
  }

  public isTrainer() {
    let isTrainer = false;
    const orgIds = this.getOrganizationIds() || [];
    for (const orgId of orgIds) {
      if(this.organizations[orgId]?.type === "trainer") {
        isTrainer = true;
        break
      }
    }
    return isTrainer
  }

  public trackingEnabled() {
    return this.plan === "personal" && this.status === "active"
  }

  public isUserClient(clientId: string): boolean {
    return this.clients && this.clients.hasOwnProperty(clientId)
  }

  public getFullName(): string {
    let name = "";
    if(this.firstName) {
      name+= this.firstName;
    }
    if(this.lastName) {
      name += ` ${this.lastName}`;
    }
    return name;
  }

  public getAge() {
    if(this.birthday) {
      return (moment as any)().diff((moment as any)(this.birthday, "YYYY-MM-DD"), 'years');
    }
    return undefined
  }

  public getTrainerIds(): string[] {
    return this.trainers && Object.keys(this.trainers).length > 0? Object.keys(this.trainers) : null;
  }

  public toJson(): Partial<User> {
    const toReturn = Object.assign({}, this);
    delete toReturn.$id;
    delete toReturn.uid;
    return toReturn
  }

  public getHeightInFeet() {
    return this.height? {feet: Math.floor(this.height/12), inches: this.height%12} : null
  }
}

export class OrganizationUser {
  public uid: string;
  public $id: string;
  public id: string;
  public deletionDate?: string;
  public firstName: string;
  public lastName: string;
  public email: string;
  public phone?: string;
  public heightUnit?: "in";
  public occupation?: string;
  public address?: string;
  public profileComplete?: boolean;
  public fullAccess?: boolean;
  public type: {client?: boolean, trainer?: boolean};
  public cancelationDate: string;
  public clientType: "in_person" | "online";
  public colorSwitcher?: boolean;
  public organizations: {
    [orgName: string]: IUserOrganization
  };
  public preferedUnits?: "imperial" | "metric";
  public preferredLanguage?: string;
  public trainers?: {} = {};
  public clients?: {} = {};
  public createdBy: string;
  public createdOn: string;
  public birthday: string;
  public notifications?: any;
  public marketing?: boolean;
  public plan?: "personal";
  public height?: any;
  public bodyType?: "ectomorph" | "mesomorph" | "endomorph";
  public profilePicture? : string;
  public gender: "male" | "female";
  public service?: string;
  public status: "active" | "inactive" | "disabled" | "lead";
  public instagram?: string;
  public flagged?: any;
  public actions: {
    equipmentList?: boolean | string,
    foodPreferences?: boolean | string,
    terms?: boolean;
  };
  public progressReports: ProgressReport[];
  public activityLevel?: "sedentary" | "active" | "lightlyActive" | "veryActive";
  /** Per-client check-in form override (falls back to org progressReports.followUpForm). */
  public checkInForm?: string;
  /**
   * Per-client override for the 4 client-overview snapshot stats.
   * Applies to all trainers in the organization. Falls back to
   * organizations/{orgId}/settings/tracking.overviewStats when unset.
   */
  public overviewStats?: string[];
  public terms? : {
    status: "accepted",
    timestamp: string;
  }
  
  constructor (init: Partial<OrganizationUser>){
      Object.assign(this, init);
  }

  public getDefaultOrganization() {
    if(this.organizations) {
      let name = null;
      let keys = Object.keys(this.organizations);
      if(keys.length === 1) {
        return name = keys[0];
      } else {
        for (const key of keys) {
          if (!WHITE_LABEL_ORGANIZATION_IDS.has(key)) {
            name = key;
            break;
          }
        }
      }
        return name
    } else {
      return undefined
    }
  }

  public getOrganizationIds(): string[] {
    if(this.organizations && Object.keys(this.organizations).length > 0) {
      return Object.keys(this.organizations);
    } else {
      return null
    }
  }

  public getOrganizations(): {id: string, status: IUserOrganization["status"], type: IUserOrganization["type"]}[] {
    if(this.organizations && Object.keys(this.organizations).length > 0) {
      const toReturn = [];
      const orgIds = Object.keys(this.organizations);
      for (const orgId of orgIds) {
        toReturn.push({id: orgId, ...this.organizations[orgId]});
      }
      return toReturn;
    } else {
      return null
    }
  }

  public getOrganization(orgName: string) {
    return this.organizations? this.organizations[orgName] : null;
  }

  public getOrganizationStatus(orgName: string): IUserOrganization["status"] {
    return this.organizations && this.organizations[orgName] && this.organizations[orgName].status? this.organizations[orgName].status : null;
  }

  getClients(): string[] {
    let clients = [];
    if(this.clients && Object.keys(this.clients).length > 0) {
      for (const clientId in this.clients) {
        if (this.clients.hasOwnProperty(clientId)) {
          clients.push(clientId);
        }
      }
    } 
    return clients
  }

  public isClient() {
    let isClient = false;
    const orgIds = this.getOrganizationIds() || [];
    for (const orgId of orgIds) {
      if(this.organizations[orgId]?.type === "client") {
        isClient = true;
        break
      }
    }
    return isClient
  }

  public isTrainer() {
    let isTrainer = false;
    const orgIds = this.getOrganizationIds() || [];
    for (const orgId of orgIds) {
      if(this.organizations[orgId]?.type === "trainer") {
        isTrainer = true;
        break
      }
    }
    return isTrainer
  }

  public trackingEnabled() {
    return this.plan === "personal" && this.status === "active"
  }

  public isUserClient(clientId: string): boolean {
    return this.clients && this.clients.hasOwnProperty(clientId)
  }

  public getFullName(): string {
    let name = "";
    if(this.firstName) {
      name+= this.firstName;
    }
    if(this.lastName) {
      name += ` ${this.lastName}`;
    }
    return name;
  }

  public getAge() {
    if(this.birthday) {
      return (moment as any)().diff((moment as any)(this.birthday, "YYYY-MM-DD"), 'years');
    }
    return undefined
  }

  public getTrainerIds(): string[] {
    return this.trainers && Object.keys(this.trainers).length > 0? Object.keys(this.trainers) : null;
  }

  public toJson(): Partial<OrganizationUser> {
    const toReturn = Object.assign({}, this);
    delete toReturn.$id;
    delete toReturn.uid;
    return toReturn
  }

  public getHeightInFeet() {
    return this.height? {feet: Math.floor(this.height/12), inches: this.height%12} : null
  }
}